import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, CircularProgress, Tooltip, Paper, Tabs, Tab } from '@mui/material';
import { useSnackbar } from 'notistack';

// Icons
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import StoreIcon from '@mui/icons-material/Store';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import GavelIcon from '@mui/icons-material/Gavel';
import WarningIcon from '@mui/icons-material/Warning';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import * as inspectionService from '../services/inspection.service';
import { toPersianDigits } from '../components/common/JalaliDatePicker';
import { ConfirmDialog } from '../components/common';

import './EntityManagement.css';
import AssigneeCell from '../components/common/AssigneeCell';

// ==================== INTERFACES ====================

interface InspectorStore {
  _id: string;
  name: string;
  slug: string;
  isInspector: boolean;
  inspectorSettings?: {
    inspectionFee: number;
    shippingCostToBuyer: number;
    inspectorCommissionSplit: number;
    totalInspections: number;
    approvedInspections: number;
    avgInspectionDays: number;
    isActive: boolean;
  };
  owner?: {
    _id: string;
    username: string;
    email: string;
  };
  postalCode?: string;
  city?: string;
  province?: string;
}

interface InspectionItem {
  _id: string;
  productTitle: string;
  storeName: string;
  quantity: number;
  totalPrice: number;
  inspectionInfo: {
    requested: boolean;
    inspectorStoreId: string;
    inspectorAddress: {
      storeName: string;
      phone: string;
      province: string;
      city: string;
      address: string;
      postalCode: string;
    };
    inspectionFee: number;
    inspectorShippingCost: number;
    inspectorCommissionSplit: number;
    status: string;
    inspectionResult?: string;
    inspectionNotes?: string;
    shippedToInspectorAt?: string;
    receivedByInspectorAt?: string;
    inspectionCompletedAt?: string;
    shippedToBuyerAt?: string;
    buyerReceivedAt?: string;
    feeRefunded?: boolean;
    feeRefundedAt?: string;
    feeRefundedAmount?: number;
    feeRefundReason?: string;
  };
  orderId?: string;
  orderNumber?: string;
}

interface InspectionPenalty {
  _id: string;
  inspectorStoreId: string | { _id: string; name: string };
  orderItemId: string | { _id: string; productTitle: string; quantity: number };
  type: 'late_inspection' | 'late_shipment';
  inspectionFee: number;
  quantity: number;
  daysLate: number;
  penaltyPctPerDay: number;
  totalPenalty: number;
  status: 'pending' | 'approved' | 'waived';
  adminNote?: string;
  resolvedAt?: string;
  createdAt: string;
}

// ==================== STATUS MAPS ====================

const INSPECTION_STATUS_MAP: Record<string, { label: string; tag: string }> = {
  pending_shipment_to_inspector: { label: 'منتظر ارسال فروشنده', tag: 'tag-warning' },
  shipped_to_inspector: { label: 'ارسال شده به بازرس', tag: 'tag-platform' },
  received_by_inspector: { label: 'دریافت شده توسط بازرس', tag: 'tag-platform' },
  inspecting: { label: 'در حال بررسی', tag: 'tag-platform' },
  inspection_in_progress: { label: 'در حال بررسی', tag: 'tag-platform' },
  approved: { label: 'تایید شده', tag: 'tag-success' },
  rejected: { label: 'رد شده', tag: 'tag-danger' },
  shipping_to_buyer: { label: 'در حال ارسال به خریدار', tag: 'tag-platform' },
  shipped_to_buyer: { label: 'ارسال شده به خریدار', tag: 'tag-platform' },
  returning_to_seller: { label: 'در حال برگشت به فروشنده', tag: 'tag-warning' },
  returned_to_seller: { label: 'برگشت به فروشنده', tag: 'tag-warning' },
  buyer_received: { label: 'تحویل خریدار', tag: 'tag-success' },
  completed: { label: 'تکمیل شده', tag: 'tag-success' },
  cancelled: { label: 'لغو شده', tag: 'tag-danger' },
};

const formatToman = (v?: number | null) =>
  v != null ? `${toPersianDigits(Number(v).toLocaleString('fa-IR'))} ت` : '-';

// ==================== COMPONENT ====================

const InspectorManagement: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [tabValue, setTabValue] = useState<number>(0);

  // Inspector List State
  const [inspectors, setInspectors] = useState<InspectorStore[]>([]);
  const [inspectorsLoading, setInspectorsLoading] = useState(true);
  const [storeSearch, setStoreSearch] = useState('');
  const [storeSearchInput, setStoreSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | 'active' | 'inactive' | 'not_inspector'>('');
  const [storePage, setStorePage] = useState(1);
  const [storePageSize, setStorePageSize] = useState(20);

  // Inspections List State
  const [inspections, setInspections] = useState<InspectionItem[]>([]);
  const [inspectionsLoading, setInspectionsLoading] = useState(false);
  const [inspectionPage, setInspectionPage] = useState(1);
  const [inspectionTotalPages, setInspectionTotalPages] = useState(1);
  const [inspectionStatusFilter, setInspectionStatusFilter] = useState('');
  const [inspectionRefundFilter, setInspectionRefundFilter] = useState<'' | 'true' | 'false'>('');

  // Toggle Dialog State
  const [toggleDialogOpen, setToggleDialogOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState<InspectorStore | null>(null);
  const [commissionSplit, setCommissionSplit] = useState(10);
  const [toggleLoading, setToggleLoading] = useState(false);

  // Override Dialog State
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState<InspectionItem | null>(null);
  const [overrideResult, setOverrideResult] = useState<'approved' | 'rejected'>('approved');
  const [overrideNotes, setOverrideNotes] = useState('');
  const [overrideLoading, setOverrideLoading] = useState(false);

  // Remove Inspector Confirm
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);

  // Penalties State
  const [penalties, setPenalties] = useState<InspectionPenalty[]>([]);
  const [penaltiesLoading, setPenaltiesLoading] = useState(false);
  const [penaltyPage, setPenaltyPage] = useState(1);
  const [penaltyTotalPages, setPenaltyTotalPages] = useState(1);
  const [penaltyStatusFilter, setPenaltyStatusFilter] = useState('');
  const [penaltyTypeFilter, setPenaltyTypeFilter] = useState('');
  const [penaltyTotalPending, setPenaltyTotalPending] = useState(0);

  // Resolve Penalty Dialog
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [selectedPenalty, setSelectedPenalty] = useState<InspectionPenalty | null>(null);
  const [resolveAction, setResolveAction] = useState<'approve' | 'waive'>('approve');
  const [resolveNote, setResolveNote] = useState('');
  const [resolveLoading, setResolveLoading] = useState(false);

  // Add Inspector Dialog State
  const [addInspectorOpen, setAddInspectorOpen] = useState(false);
  const [addSearchInput, setAddSearchInput] = useState('');
  const [addSearchResults, setAddSearchResults] = useState<InspectorStore[]>([]);
  const [addSearchLoading, setAddSearchLoading] = useState(false);
  const [addSearched, setAddSearched] = useState(false);
  const [addSelectedStore, setAddSelectedStore] = useState<InspectorStore | null>(null);
  const [addCommissionSplit, setAddCommissionSplit] = useState(10);
  const [addSubmitting, setAddSubmitting] = useState(false);

  // ==================== DATA FETCHING ====================

  const fetchInspectors = useCallback(async (search?: string) => {
    setInspectorsLoading(true);
    try {
      const params: any = {};
      if (search) {
        params.search = search;
        params.onlyInspectors = 'false';
      }
      const res = await inspectionService.getInspectorList(params);
      const data = res?.inspectors || res?.data || res || [];
      setInspectors(Array.isArray(data) ? data : []);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'خطا در دریافت لیست بازرسان', { variant: 'error' });
    } finally {
      setInspectorsLoading(false);
    }
  }, [enqueueSnackbar]);

  const fetchInspections = useCallback(async () => {
    setInspectionsLoading(true);
    try {
      const params: any = { page: inspectionPage, limit: 20 };
      if (inspectionStatusFilter) params.status = inspectionStatusFilter;
      if (inspectionRefundFilter) params.feeRefunded = inspectionRefundFilter;
      const res = await inspectionService.getAllInspections(params);
      const data = res?.items || res?.data?.items || res?.data || res || [];
      setInspections(Array.isArray(data) ? data : []);
      setInspectionTotalPages(
        res?.pagination?.totalPages ||
        res?.data?.pagination?.totalPages ||
        res?.totalPages ||
        1
      );
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'خطا در دریافت بازرسی‌ها', { variant: 'error' });
    } finally {
      setInspectionsLoading(false);
    }
  }, [enqueueSnackbar, inspectionPage, inspectionStatusFilter, inspectionRefundFilter]);

  const fetchPenalties = useCallback(async () => {
    setPenaltiesLoading(true);
    try {
      const params: any = { page: penaltyPage, limit: 20 };
      if (penaltyStatusFilter) params.status = penaltyStatusFilter;
      if (penaltyTypeFilter) params.type = penaltyTypeFilter;
      const res = await inspectionService.getPenalties(params);
      const data = res?.penalties || res?.data?.penalties || [];
      setPenalties(Array.isArray(data) ? data : []);
      setPenaltyTotalPages(res?.totalPages || res?.data?.totalPages || 1);
      const pending = (Array.isArray(data) ? data : [])
        .filter((p: InspectionPenalty) => p.status === 'pending')
        .reduce((sum: number, p: InspectionPenalty) => sum + (p.totalPenalty || 0), 0);
      setPenaltyTotalPending(pending);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'خطا در دریافت جریمه‌ها', { variant: 'error' });
    } finally {
      setPenaltiesLoading(false);
    }
  }, [enqueueSnackbar, penaltyPage, penaltyStatusFilter, penaltyTypeFilter]);

  useEffect(() => {
    fetchInspectors();
  }, [fetchInspectors]);

  useEffect(() => {
    if (tabValue === 1) fetchInspections();
  }, [tabValue, fetchInspections]);

  useEffect(() => {
    if (tabValue === 2) fetchPenalties();
  }, [tabValue, fetchPenalties]);

  // ==================== HANDLERS ====================

  const handleSearchStore = () => {
    setStoreSearch(storeSearchInput);
    setStorePage(1);
    fetchInspectors(storeSearchInput || undefined);
  };

  const handleSearchKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearchStore();
  };

  const clearFilters = () => {
    setStoreSearch('');
    setStoreSearchInput('');
    setStatusFilter('');
    setStorePage(1);
    fetchInspectors();
  };

  const handleOpenToggleDialog = (store: InspectorStore) => {
    setSelectedStore(store);
    setCommissionSplit(store.inspectorSettings?.inspectorCommissionSplit || 10);
    setToggleDialogOpen(true);
  };

  const handleToggleInspector = async () => {
    if (!selectedStore) return;
    setToggleLoading(true);
    try {
      const newStatus = !selectedStore.isInspector;
      await inspectionService.toggleInspector({
        storeId: selectedStore._id,
        isInspector: newStatus,
        commissionSplit,
      });
      enqueueSnackbar(
        newStatus
          ? `فروشگاه "${selectedStore.name}" به عنوان بازرس تعیین شد`
          : `وضعیت بازرس فروشگاه "${selectedStore.name}" حذف شد`,
        { variant: 'success' }
      );
      setToggleDialogOpen(false);
      setRemoveConfirmOpen(false);
      fetchInspectors(storeSearch || undefined);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'خطا در تغییر وضعیت بازرس', { variant: 'error' });
    } finally {
      setToggleLoading(false);
    }
  };

  const handleOpenOverrideDialog = (item: InspectionItem) => {
    setSelectedInspection(item);
    setOverrideResult('approved');
    setOverrideNotes('');
    setOverrideDialogOpen(true);
  };

  const handleOverrideResult = async () => {
    if (!selectedInspection) return;
    setOverrideLoading(true);
    try {
      await inspectionService.overrideInspectionResult({
        orderItemId: selectedInspection._id,
        result: overrideResult,
        notes: overrideNotes || undefined,
      });
      enqueueSnackbar('نتیجه بازرسی با موفقیت اورراید شد', { variant: 'success' });
      setOverrideDialogOpen(false);
      fetchInspections();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'خطا در اورراید نتیجه', { variant: 'error' });
    } finally {
      setOverrideLoading(false);
    }
  };

  const handleOpenResolveDialog = (penalty: InspectionPenalty, action: 'approve' | 'waive') => {
    setSelectedPenalty(penalty);
    setResolveAction(action);
    setResolveNote('');
    setResolveDialogOpen(true);
  };

  const handleResolvePenalty = async () => {
    if (!selectedPenalty) return;
    setResolveLoading(true);
    try {
      await inspectionService.resolvePenalty(selectedPenalty._id, {
        action: resolveAction,
        note: resolveNote || undefined,
      });
      enqueueSnackbar(resolveAction === 'approve' ? 'جریمه تایید شد' : 'جریمه بخشیده شد', { variant: 'success' });
      setResolveDialogOpen(false);
      fetchPenalties();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'خطا در تکلیف جریمه', { variant: 'error' });
    } finally {
      setResolveLoading(false);
    }
  };

  // ==================== ADD INSPECTOR HANDLERS ====================

  const handleOpenAddInspector = () => {
    setAddSearchInput('');
    setAddSearchResults([]);
    setAddSearched(false);
    setAddSelectedStore(null);
    setAddCommissionSplit(10);
    setAddInspectorOpen(true);
  };

  const handleSearchAddInspector = async () => {
    const term = addSearchInput.trim();
    if (!term) {
      enqueueSnackbar('عبارت جستجو را وارد کنید', { variant: 'warning' });
      return;
    }
    setAddSearchLoading(true);
    setAddSearched(true);
    try {
      const res = await inspectionService.getInspectorList({
        search: term,
        onlyInspectors: 'false',
      });
      const data: InspectorStore[] = res?.inspectors || res?.data || res || [];
      const nonInspectors = (Array.isArray(data) ? data : []).filter((s) => !s.isInspector);
      setAddSearchResults(nonInspectors);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'خطا در جستجوی فروشگاه‌ها', { variant: 'error' });
      setAddSearchResults([]);
    } finally {
      setAddSearchLoading(false);
    }
  };

  const handleAddSearchKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearchAddInspector();
  };

  const handleConfirmAddInspector = async () => {
    if (!addSelectedStore) return;
    if (!addSelectedStore.postalCode) {
      enqueueSnackbar('این فروشگاه کد پستی ندارد و نمی‌تواند بازرس شود', { variant: 'error' });
      return;
    }
    setAddSubmitting(true);
    try {
      await inspectionService.toggleInspector({
        storeId: addSelectedStore._id,
        isInspector: true,
        commissionSplit: addCommissionSplit,
      });
      enqueueSnackbar(`فروشگاه "${addSelectedStore.name}" به عنوان بازرس تعیین شد`, {
        variant: 'success',
      });
      setAddInspectorOpen(false);
      fetchInspectors(storeSearch || undefined);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'خطا در تعیین بازرس', { variant: 'error' });
    } finally {
      setAddSubmitting(false);
    }
  };

  const getPenaltyInspectorName = (p: InspectionPenalty) =>
    typeof p.inspectorStoreId === 'object' ? p.inspectorStoreId.name : p.inspectorStoreId;

  const getPenaltyProductTitle = (p: InspectionPenalty) =>
    typeof p.orderItemId === 'object' ? p.orderItemId.productTitle : '—';

  // ==================== FILTERING ====================

  const filteredInspectors = inspectors.filter((s) => {
    if (statusFilter === 'active') {
      if (!(s.isInspector && s.inspectorSettings?.isActive)) return false;
    } else if (statusFilter === 'inactive') {
      if (!(s.isInspector && !s.inspectorSettings?.isActive)) return false;
    } else if (statusFilter === 'not_inspector') {
      if (s.isInspector) return false;
    }
    return true;
  });

  const totalStores = filteredInspectors.length;
  const totalStorePages = Math.max(1, Math.ceil(totalStores / storePageSize));
  const startItem = totalStores === 0 ? 0 : (storePage - 1) * storePageSize + 1;
  const endItem = Math.min(storePage * storePageSize, totalStores);
  const paginatedStores = filteredInspectors.slice((storePage - 1) * storePageSize, storePage * storePageSize);

  const hasActiveFilters = !!(storeSearch || statusFilter);

  // Stats
  const activeInspectorsCount = inspectors.filter(
    (i) => i.isInspector && i.inspectorSettings?.isActive
  ).length;
  const totalInspectorsCount = inspectors.filter((i) => i.isInspector).length;
  const totalApprovedInspections = inspectors.reduce(
    (sum, i) => sum + (i.inspectorSettings?.approvedInspections || 0),
    0
  );
  const totalInspectionsCount = inspectors.reduce(
    (sum, i) => sum + (i.inspectorSettings?.totalInspections || 0),
    0
  );

  const pendingPenaltiesCount = penalties.filter((p) => p.status === 'pending').length;

  // ==================== RENDER ====================

  return (
    <div className="entity-page">
      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">مدیریت بازرسی</h1>
          <p className="entity-subtitle">مدیریت فروشگاه‌های بازرس و نظارت بر بازرسی‌ها</p>
        </div>
        <div className="header-actions">
          <button
            className="header-btn primary"
            onClick={handleOpenAddInspector}
            title="افزودن بازرس جدید"
          >
            <AddIcon fontSize="small" />
            <span style={{ marginRight: 6 }}>افزودن بازرس</span>
          </button>
          <button
            className="header-btn refresh"
            onClick={() => {
              fetchInspectors(storeSearch || undefined);
              if (tabValue === 1) fetchInspections();
              if (tabValue === 2) fetchPenalties();
            }}
            title="بروزرسانی"
          >
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><VerifiedUserIcon /></div>
          <div>
            <p className="entity-stat-label">بازرسان فعال</p>
            <p className="entity-stat-value">{toPersianDigits(String(activeInspectorsCount))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon green"><CheckCircleIcon /></div>
          <div>
            <p className="entity-stat-label">بازرسی‌های تایید شده</p>
            <p className="entity-stat-value">{toPersianDigits(String(totalApprovedInspections))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon orange"><TrendingUpIcon /></div>
          <div>
            <p className="entity-stat-label">کل بازرسی‌ها</p>
            <p className="entity-stat-value">{toPersianDigits(String(totalInspectionsCount))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon red"><GavelIcon /></div>
          <div>
            <p className="entity-stat-label">جریمه‌های در انتظار</p>
            <p className="entity-stat-value">{toPersianDigits(String(pendingPenaltiesCount))}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Paper sx={{ mb: 2, borderRadius: '12px' }}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          sx={{ px: 2, borderBottom: '1px solid var(--color-border)' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<StoreIcon fontSize="small" />} iconPosition="start" label="فروشگاه‌های بازرس" />
          <Tab icon={<SearchIcon fontSize="small" />} iconPosition="start" label="بازرسی‌های فعال" />
          <Tab
            icon={<GavelIcon fontSize="small" />}
            iconPosition="start"
            label={pendingPenaltiesCount > 0 ? `جریمه‌ها (${toPersianDigits(String(pendingPenaltiesCount))})` : 'جریمه‌ها'}
          />
        </Tabs>
      </Paper>

      {/* TAB 0: Inspector Stores */}
      {tabValue === 0 && (
        <>
          {/* Filters */}
          <div className="entity-filters">
            <div className="filters-grid">
              <div className="filter-group">
                <label className="filter-label">
                  <SearchIcon fontSize="small" />
                  جستجو
                </label>
                <div className="filter-search-wrap">
                  <input
                    className="filter-field"
                    type="text"
                    placeholder="جستجوی فروشگاه برای تعیین بازرس..."
                    value={storeSearchInput}
                    onChange={(e) => setStoreSearchInput(e.target.value)}
                    onKeyPress={handleSearchKey}
                  />
                  <span className="filter-search-icon">
                    <SearchIcon fontSize="small" />
                  </span>
                </div>
              </div>

              <div className="filter-group">
                <label className="filter-label">وضعیت بازرس</label>
                <select
                  className="filter-field"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value as any);
                    setStorePage(1);
                  }}
                >
                  <option value="">همه وضعیت‌ها</option>
                  <option value="active">بازرس فعال</option>
                  <option value="inactive">بازرس غیرفعال</option>
                  <option value="not_inspector">بازرس نیست</option>
                </select>
              </div>

              <div className="filter-group filter-group-actions">
                <button className="filter-btn" onClick={handleSearchStore}>
                  <SearchIcon fontSize="small" />
                  اعمال
                </button>
                {hasActiveFilters && (
                  <button className="filter-btn outline" onClick={clearFilters}>
                    پاک
                  </button>
                )}
              </div>
            </div>

            {hasActiveFilters && (
              <div className="filters-active">
                <span className="filters-label">فیلترهای فعال:</span>
                {storeSearch && (
                  <span className="filter-tag">
                    جستجو: {storeSearch}
                    <button onClick={() => { setStoreSearch(''); setStoreSearchInput(''); fetchInspectors(); }}>×</button>
                  </span>
                )}
                {statusFilter && (
                  <span className="filter-tag">
                    وضعیت: {statusFilter === 'active' ? 'بازرس فعال' : statusFilter === 'inactive' ? 'بازرس غیرفعال' : 'بازرس نیست'}
                    <button onClick={() => setStatusFilter('')}>×</button>
                  </span>
                )}
                <button className="filters-clear" onClick={clearFilters}>پاک کردن همه</button>
              </div>
            )}
          </div>

          {/* Table */}
          <div className="entity-table-wrapper">
            {inspectorsLoading ? (
              <div className="entity-loading"><CircularProgress size={32} /></div>
            ) : paginatedStores.length === 0 ? (
              <div className="entity-empty">
                <div className="entity-empty-icon"><StoreIcon style={{ fontSize: 48 }} /></div>
                <p className="entity-empty-text">
                  {storeSearch ? `فروشگاهی با نام "${storeSearch}" یافت نشد` : 'هیچ فروشگاهی یافت نشد'}
                </p>
              </div>
            ) : (
              <>
                <table className="entity-table">
                  <thead>
                    <tr>
                      <th>فروشگاه</th>
                      <th>مالک</th>
                      <th>وضعیت بازرس</th>
                      <th style={{ textAlign: 'center' }}>هزینه بازرسی</th>
                      <th style={{ textAlign: 'center' }}>هزینه ارسال</th>
                      <th style={{ textAlign: 'center' }}>سهم کمیسیون</th>
                      <th style={{ textAlign: 'center' }}>تعداد بازرسی</th>
                      <th style={{ textAlign: 'center' }}>نرخ تایید</th>
                      <th style={{ width: 120 }}>عملیات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedStores.map((store) => {
                      const settings = store.inspectorSettings;
                      const approvalRate = settings && settings.totalInspections > 0
                        ? Math.round((settings.approvedInspections / settings.totalInspections) * 100)
                        : 0;
                      return (
                        <tr key={store._id}>
                          <td>
                            <div className="entity-info-cell">
                              <div className="entity-image"><StoreIcon /></div>
                              <div className="entity-info">
                                <span className="entity-name">{store.name}</span>
                                <span className="entity-desc">
                                  {store.province && store.city
                                    ? `${store.province}، ${store.city}`
                                    : 'آدرس ثبت نشده'}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="entity-info">
                              <span className="entity-name" style={{ fontSize: 13 }}>{store.owner?.username || '-'}</span>
                              <span className="entity-desc">{store.owner?.email || ''}</span>
                            </div>
                          </td>
                          <td>
                            {store.isInspector ? (
                              <span className={`tag tag-sm ${settings?.isActive ? 'tag-active' : 'tag-inactive'}`}>
                                {settings?.isActive ? 'فعال' : 'غیرفعال'}
                              </span>
                            ) : (
                              <span className="tag tag-sm tag-inactive">بازرس نیست</span>
                            )}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span className="cell-code">{formatToman(settings?.inspectionFee)}</span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span className="cell-code">{formatToman(settings?.shippingCostToBuyer)}</span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {settings?.inspectorCommissionSplit != null
                              ? toPersianDigits(`${settings.inspectorCommissionSplit}%`)
                              : '-'}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {toPersianDigits(String(settings?.totalInspections ?? 0))}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {settings?.totalInspections ? (
                              <span
                                className={`tag tag-sm ${
                                  approvalRate >= 80
                                    ? 'tag-success'
                                    : approvalRate >= 50
                                    ? 'tag-warning'
                                    : 'tag-danger'
                                }`}
                              >
                                {toPersianDigits(`${approvalRate}%`)}
                              </span>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td>
                            <div className="actions-cell">
                              <Tooltip title={store.isInspector ? 'ویرایش / حذف بازرس' : 'تعیین بازرس'} arrow>
                                <button className="action-btn edit" onClick={() => handleOpenToggleDialog(store)}>
                                  <EditIcon />
                                </button>
                              </Tooltip>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Mobile Cards */}
                <div className="entity-cards">
                  {paginatedStores.map((store) => {
                    const settings = store.inspectorSettings;
                    const approvalRate = settings && settings.totalInspections > 0
                      ? Math.round((settings.approvedInspections / settings.totalInspections) * 100)
                      : 0;
                    return (
                      <div key={store._id} className={`entity-card ${!store.isInspector ? 'inactive' : ''}`}>
                        <div className="entity-card-header">
                          <div className="entity-card-image"><StoreIcon /></div>
                          <div className="entity-card-info">
                            <div className="entity-card-name">{store.name}</div>
                            <div className="entity-card-desc">
                              {store.owner?.username || '-'} • {store.province && store.city ? `${store.province}، ${store.city}` : 'آدرس ثبت نشده'}
                            </div>
                            <div className="entity-card-desc">
                              کمیسیون: {toPersianDigits(String(settings?.inspectorCommissionSplit ?? 0))}% •
                              بازرسی‌ها: {toPersianDigits(String(settings?.totalInspections ?? 0))} •
                              تایید: {toPersianDigits(String(approvalRate))}%
                            </div>
                          </div>
                          <div className="entity-card-status">
                            {store.isInspector ? (
                              <span className={`tag tag-sm ${settings?.isActive ? 'tag-active' : 'tag-inactive'}`}>
                                {settings?.isActive ? 'فعال' : 'غیرفعال'}
                              </span>
                            ) : (
                              <span className="tag tag-sm tag-inactive">بازرس نیست</span>
                            )}
                          </div>
                        </div>
                        <div className="entity-card-actions">
                          <button className="entity-card-btn primary" onClick={() => handleOpenToggleDialog(store)}>
                            <EditIcon fontSize="small" /> {store.isInspector ? 'ویرایش' : 'تعیین بازرس'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                <div className="entity-pagination">
                  <div className="pagination-info">
                    نمایش {toPersianDigits(String(startItem))} تا {toPersianDigits(String(endItem))} از {toPersianDigits(String(totalStores))}
                  </div>
                  <div className="pagination-controls">
                    <select
                      className="pagination-select"
                      value={storePageSize}
                      onChange={(e) => { setStorePageSize(Number(e.target.value)); setStorePage(1); }}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <button className="pagination-btn" onClick={() => setStorePage(storePage + 1)} disabled={storePage >= totalStorePages}>
                      <ChevronRightIcon fontSize="small" />
                    </button>
                    <span style={{ fontSize: 12 }}>
                      {toPersianDigits(String(storePage))} / {toPersianDigits(String(totalStorePages))}
                    </span>
                    <button className="pagination-btn" onClick={() => setStorePage(storePage - 1)} disabled={storePage <= 1}>
                      <ChevronLeftIcon fontSize="small" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* TAB 1: Active Inspections */}
      {tabValue === 1 && (
        <>
          <div className="entity-filters">
            <div className="filters-grid">
              <div className="filter-group">
                <label className="filter-label">وضعیت بازرسی</label>
                <select
                  className="filter-field"
                  value={inspectionStatusFilter}
                  onChange={(e) => { setInspectionStatusFilter(e.target.value); setInspectionPage(1); }}
                >
                  <option value="">همه وضعیت‌ها</option>
                  {Object.entries(INSPECTION_STATUS_MAP).map(([key, val]) => (
                    <option key={key} value={key}>{val.label}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label className="filter-label">وضعیت بازپرداخت هزینه</label>
                <select
                  className="filter-field"
                  value={inspectionRefundFilter}
                  onChange={(e) => {
                    setInspectionRefundFilter(e.target.value as '' | 'true' | 'false');
                    setInspectionPage(1);
                  }}
                >
                  <option value="">همه</option>
                  <option value="true">بازپرداخت‌شده</option>
                  <option value="false">بدون بازپرداخت</option>
                </select>
              </div>

              <div className="filter-group filter-group-actions">
                {(inspectionStatusFilter || inspectionRefundFilter) && (
                  <button
                    className="filter-btn outline"
                    onClick={() => {
                      setInspectionStatusFilter('');
                      setInspectionRefundFilter('');
                    }}
                  >
                    پاک
                  </button>
                )}
              </div>
            </div>
            {(inspectionStatusFilter || inspectionRefundFilter) && (
              <div className="filters-active">
                <span className="filters-label">فیلترهای فعال:</span>
                {inspectionStatusFilter && (
                  <span className="filter-tag">
                    وضعیت: {INSPECTION_STATUS_MAP[inspectionStatusFilter]?.label || inspectionStatusFilter}
                    <button onClick={() => setInspectionStatusFilter('')}>×</button>
                  </span>
                )}
                {inspectionRefundFilter && (
                  <span className="filter-tag">
                    بازپرداخت: {inspectionRefundFilter === 'true' ? 'بازپرداخت‌شده' : 'بدون بازپرداخت'}
                    <button onClick={() => setInspectionRefundFilter('')}>×</button>
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="entity-table-wrapper">
            {inspectionsLoading ? (
              <div className="entity-loading"><CircularProgress size={32} /></div>
            ) : inspections.length === 0 ? (
              <div className="entity-empty">
                <div className="entity-empty-icon"><SearchIcon style={{ fontSize: 48 }} /></div>
                <p className="entity-empty-text">بازرسی فعالی یافت نشد</p>
              </div>
            ) : (
              <>
                <table className="entity-table">
                  <thead>
                    <tr>
                      <th>محصول</th>
                      <th>فروشنده</th>
                      <th>بازرس</th>
                      <th style={{ textAlign: 'center' }}>وضعیت</th>
                      <th style={{ textAlign: 'center' }}>نتیجه</th>
                      <th style={{ textAlign: 'center' }}>هزینه بازرسی</th>
                      <th style={{ textAlign: 'center' }}>وضعیت پرداخت</th>
                      <th style={{ width: 100 }}>عملیات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inspections.map((item) => {
                      const statusConfig = INSPECTION_STATUS_MAP[item.inspectionInfo?.status] || {
                        label: item.inspectionInfo?.status || '-',
                        tag: 'tag-inactive',
                      };
                      return (
                        <tr key={item._id}>
                          <td>
                            <div className="entity-info">
                              <span className="entity-name">{item.productTitle}</span>
                              {item.orderNumber && (
                                <span className="entity-desc">
                                  سفارش #{toPersianDigits(String(item.orderNumber))}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>{item.storeName}</td>
                          <td>{item.inspectionInfo?.inspectorAddress?.storeName || '-'}</td>
                          <td style={{ textAlign: 'center' }}>
                            <span className={`tag tag-sm ${statusConfig.tag}`}>{statusConfig.label}</span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {item.inspectionInfo?.inspectionResult === 'approved' ? (
                              <span className="tag tag-sm tag-success">
                                <CheckCircleIcon fontSize="inherit" /> تایید
                              </span>
                            ) : item.inspectionInfo?.inspectionResult === 'rejected' ? (
                              <span className="tag tag-sm tag-danger">
                                <CancelIcon fontSize="inherit" /> رد
                              </span>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span className="cell-code">{formatToman(item.inspectionInfo?.inspectionFee)}</span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {item.inspectionInfo?.feeRefunded ? (
                              <Tooltip
                                title={
                                  item.inspectionInfo?.feeRefundReason
                                    ? `دلیل: ${item.inspectionInfo.feeRefundReason}`
                                    : 'هزینه بازرسی به خریدار بازپرداخت شده'
                                }
                                arrow
                              >
                                <span className="tag tag-sm tag-warning">
                                  بازپرداخت‌شده
                                  {item.inspectionInfo?.feeRefundedAmount
                                    ? ` (${formatToman(item.inspectionInfo.feeRefundedAmount)})`
                                    : ''}
                                </span>
                              </Tooltip>
                            ) : (
                              <span className="tag tag-sm tag-success">پرداخت‌شده</span>
                            )}
                          </td>
                          <td>
                            <div className="actions-cell">
                              <Tooltip title="اورراید نتیجه" arrow>
                                <button className="action-btn edit" onClick={() => handleOpenOverrideDialog(item)}>
                                  <EditIcon />
                                </button>
                              </Tooltip>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {inspectionTotalPages > 1 && (
                  <div className="entity-pagination">
                    <div className="pagination-info">
                      صفحه {toPersianDigits(String(inspectionPage))} از {toPersianDigits(String(inspectionTotalPages))}
                    </div>
                    <div className="pagination-controls">
                      <button
                        className="pagination-btn"
                        onClick={() => setInspectionPage(inspectionPage + 1)}
                        disabled={inspectionPage >= inspectionTotalPages}
                      >
                        <ChevronRightIcon fontSize="small" />
                      </button>
                      <span style={{ fontSize: 12 }}>
                        {toPersianDigits(String(inspectionPage))} / {toPersianDigits(String(inspectionTotalPages))}
                      </span>
                      <button
                        className="pagination-btn"
                        onClick={() => setInspectionPage(inspectionPage - 1)}
                        disabled={inspectionPage <= 1}
                      >
                        <ChevronLeftIcon fontSize="small" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* TAB 2: Penalties */}
      {tabValue === 2 && (
        <>
          <div className="entity-filters">
            <div className="filters-grid">
              <div className="filter-group">
                <label className="filter-label">وضعیت جریمه</label>
                <select
                  className="filter-field"
                  value={penaltyStatusFilter}
                  onChange={(e) => { setPenaltyStatusFilter(e.target.value); setPenaltyPage(1); }}
                >
                  <option value="">همه وضعیت‌ها</option>
                  <option value="pending">در انتظار</option>
                  <option value="approved">تایید شده</option>
                  <option value="waived">بخشیده شده</option>
                </select>
              </div>

              <div className="filter-group">
                <label className="filter-label">نوع تاخیر</label>
                <select
                  className="filter-field"
                  value={penaltyTypeFilter}
                  onChange={(e) => { setPenaltyTypeFilter(e.target.value); setPenaltyPage(1); }}
                >
                  <option value="">همه انواع تاخیر</option>
                  <option value="late_inspection">تاخیر بازرسی</option>
                  <option value="late_shipment">تاخیر ارسال</option>
                </select>
              </div>

              <div className="filter-group filter-group-actions">
                {(penaltyStatusFilter || penaltyTypeFilter) && (
                  <button className="filter-btn outline" onClick={() => { setPenaltyStatusFilter(''); setPenaltyTypeFilter(''); }}>
                    پاک
                  </button>
                )}
              </div>
            </div>
            {(penaltyStatusFilter || penaltyTypeFilter) && (
              <div className="filters-active">
                <span className="filters-label">فیلترهای فعال:</span>
                {penaltyStatusFilter && (
                  <span className="filter-tag">
                    وضعیت: {penaltyStatusFilter === 'pending' ? 'در انتظار' : penaltyStatusFilter === 'approved' ? 'تایید شده' : 'بخشیده شده'}
                    <button onClick={() => setPenaltyStatusFilter('')}>×</button>
                  </span>
                )}
                {penaltyTypeFilter && (
                  <span className="filter-tag">
                    نوع: {penaltyTypeFilter === 'late_inspection' ? 'تاخیر بازرسی' : 'تاخیر ارسال'}
                    <button onClick={() => setPenaltyTypeFilter('')}>×</button>
                  </span>
                )}
                <button className="filters-clear" onClick={() => { setPenaltyStatusFilter(''); setPenaltyTypeFilter(''); }}>
                  پاک کردن همه
                </button>
              </div>
            )}
          </div>

          {penaltyTotalPending > 0 && (
            <div
              style={{
                background: 'var(--color-warning-bg, #fef3c7)',
                color: 'var(--color-warning-dark, #92400e)',
                padding: '12px 16px',
                borderRadius: 8,
                marginBottom: 12,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <WarningIcon />
              جمع جریمه‌های در انتظار تصمیم: {toPersianDigits(Number(penaltyTotalPending).toLocaleString('fa-IR'))} تومان
            </div>
          )}

          <div className="entity-table-wrapper">
            {penaltiesLoading ? (
              <div className="entity-loading"><CircularProgress size={32} /></div>
            ) : penalties.length === 0 ? (
              <div className="entity-empty">
                <div className="entity-empty-icon"><CheckCircleIcon style={{ fontSize: 48 }} /></div>
                <p className="entity-empty-text">هیچ جریمه‌ای یافت نشد</p>
              </div>
            ) : (
              <>
                <table className="entity-table">
                  <thead>
                    <tr>
                      <th>بازرس</th>
                      <th>محصول</th>
                      <th style={{ textAlign: 'center' }}>نوع تاخیر</th>
                      <th style={{ textAlign: 'center' }}>روزهای تاخیر</th>
                      <th style={{ textAlign: 'center' }}>درصد جریمه/روز</th>
                      <th style={{ textAlign: 'center' }}>جمع جریمه</th>
                      <th style={{ textAlign: 'center' }}>وضعیت</th>
                      <th>مسئول</th>
                      <th style={{ width: 120 }}>عملیات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {penalties.map((penalty) => {
                      const isPending = penalty.status === 'pending';
                      return (
                        <tr key={penalty._id}>
                          <td>
                            <div className="entity-info">
                              <span className="entity-name" style={{ fontSize: 13 }}>
                                {getPenaltyInspectorName(penalty)}
                              </span>
                            </div>
                          </td>
                          <td>
                            <div className="entity-info">
                              <span className="entity-name" style={{ fontSize: 13 }}>{getPenaltyProductTitle(penalty)}</span>
                              <span className="entity-desc">
                                {toPersianDigits(String(penalty.quantity))} عدد · هزینه بازرسی: {formatToman(penalty.inspectionFee)}
                              </span>
                            </div>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span className={`tag tag-sm ${penalty.type === 'late_inspection' ? 'tag-warning' : 'tag-danger'}`}>
                              {penalty.type === 'late_inspection' ? 'تاخیر بازرسی' : 'تاخیر ارسال'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center', fontWeight: 700, color: penalty.daysLate >= 4 ? '#dc2626' : '#ea580c' }}>
                            {toPersianDigits(String(penalty.daysLate))} روز
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {toPersianDigits(`${penalty.penaltyPctPerDay}٪`)}
                          </td>
                          <td style={{ textAlign: 'center', fontWeight: 700, color: isPending ? '#dc2626' : undefined }}>
                            {formatToman(penalty.totalPenalty)}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span
                              className={`tag tag-sm ${
                                penalty.status === 'pending'
                                  ? 'tag-warning'
                                  : penalty.status === 'approved'
                                  ? 'tag-danger'
                                  : 'tag-success'
                              }`}
                            >
                              {penalty.status === 'pending' ? 'در انتظار' : penalty.status === 'approved' ? 'تایید شده' : 'بخشیده شده'}
                            </span>
                            {penalty.adminNote && (
                              <Tooltip title={penalty.adminNote} arrow>
                                <span className="entity-desc" style={{ display: 'block', marginTop: 4, cursor: 'help' }}>
                                  یادداشت...
                                </span>
                              </Tooltip>
                            )}
                          </td>
                          <td><AssigneeCell value={(penalty as any).assignedTo} /></td>
                          <td>
                            <div className="actions-cell">
                              {isPending ? (
                                <>
                                  <Tooltip title="تایید جریمه" arrow>
                                    <button
                                      className="action-btn delete"
                                      onClick={() => handleOpenResolveDialog(penalty, 'approve')}
                                    >
                                      <GavelIcon />
                                    </button>
                                  </Tooltip>
                                  <Tooltip title="بخشودگی جریمه" arrow>
                                    <button
                                      className="action-btn edit"
                                      onClick={() => handleOpenResolveDialog(penalty, 'waive')}
                                    >
                                      <CheckCircleIcon />
                                    </button>
                                  </Tooltip>
                                </>
                              ) : (
                                <span className="entity-desc">
                                  {penalty.resolvedAt
                                    ? toPersianDigits(new Date(penalty.resolvedAt).toLocaleDateString('fa-IR'))
                                    : '—'}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {penaltyTotalPages > 1 && (
                  <div className="entity-pagination">
                    <div className="pagination-info">
                      صفحه {toPersianDigits(String(penaltyPage))} از {toPersianDigits(String(penaltyTotalPages))}
                    </div>
                    <div className="pagination-controls">
                      <button
                        className="pagination-btn"
                        onClick={() => setPenaltyPage(penaltyPage + 1)}
                        disabled={penaltyPage >= penaltyTotalPages}
                      >
                        <ChevronRightIcon fontSize="small" />
                      </button>
                      <span style={{ fontSize: 12 }}>
                        {toPersianDigits(String(penaltyPage))} / {toPersianDigits(String(penaltyTotalPages))}
                      </span>
                      <button
                        className="pagination-btn"
                        onClick={() => setPenaltyPage(penaltyPage - 1)}
                        disabled={penaltyPage <= 1}
                      >
                        <ChevronLeftIcon fontSize="small" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Toggle Inspector Dialog */}
      <Dialog
        open={toggleDialogOpen}
        onClose={() => setToggleDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        className="inspector-edit-dialog"
      >
        <div className="edit-dialog-title">
          <span>{selectedStore?.isInspector ? 'ویرایش تنظیمات بازرس' : 'تعیین به عنوان بازرس'}</span>
          <button className="details-close" onClick={() => setToggleDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {selectedStore && (
            <div className="edit-form">
              {/* Store Info */}
              <div className="edit-card">
                <div className="edit-card-header">اطلاعات فروشگاه</div>
                <div className="edit-card-body">
                  <div className="entity-info-cell" style={{ marginBottom: 12 }}>
                    <div className="entity-image"><StoreIcon /></div>
                    <div className="entity-info">
                      <span className="entity-name">{selectedStore.name}</span>
                      <span className="entity-desc">
                        مالک: {selectedStore.owner?.username || '-'}
                      </span>
                      <span className="entity-desc">
                        آدرس: {selectedStore.province && selectedStore.city ? `${selectedStore.province}، ${selectedStore.city}` : 'ثبت نشده'}
                      </span>
                    </div>
                  </div>
                  {!selectedStore.postalCode && (
                    <div
                      style={{
                        background: 'var(--color-warning-bg, #fef3c7)',
                        color: 'var(--color-warning-dark, #92400e)',
                        padding: '10px 12px',
                        borderRadius: 8,
                        fontSize: 13,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <WarningIcon fontSize="small" />
                      این فروشگاه کد پستی ندارد. برای فعالیت به عنوان بازرس، کد پستی الزامی است.
                    </div>
                  )}
                </div>
              </div>

              {/* Commission Settings */}
              <div className="edit-card">
                <div className="edit-card-header">تنظیمات کمیسیون</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field full">
                      <label>سهم کمیسیون بازرس (%)</label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={commissionSplit}
                        onChange={(e) =>
                          setCommissionSplit(Math.max(0, Math.min(100, Number(e.target.value))))
                        }
                      />
                      <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                        درصد کمیسیون فروشنده که به بازرس تعلق می‌گیرد
                      </span>
                    </div>
                  </div>

                  {selectedStore.isInspector && (
                    <div
                      style={{
                        background: 'var(--color-primary-bg, #FCE8EE)',
                        color: 'var(--color-primary, #A84B6A)',
                        padding: '10px 12px',
                        borderRadius: 8,
                        fontSize: 13,
                        marginTop: 12,
                      }}
                    >
                      وضعیت فعلی: بازرس فعال
                      {selectedStore.inspectorSettings?.totalInspections
                        ? ` - ${toPersianDigits(String(selectedStore.inspectorSettings.totalInspections))} بازرسی انجام شده`
                        : ''}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => setToggleDialogOpen(false)}>
            انصراف
          </button>
          {selectedStore?.isInspector ? (
            <>
              <button
                className="edit-btn submit"
                onClick={async () => {
                  // Update (keep as inspector, just update commission)
                  if (!selectedStore) return;
                  setToggleLoading(true);
                  try {
                    await inspectionService.toggleInspector({
                      storeId: selectedStore._id,
                      isInspector: true,
                      commissionSplit,
                    });
                    enqueueSnackbar('تنظیمات بازرس به‌روزرسانی شد', { variant: 'success' });
                    setToggleDialogOpen(false);
                    fetchInspectors(storeSearch || undefined);
                  } catch (err: any) {
                    enqueueSnackbar(err?.message || 'خطا در به‌روزرسانی', { variant: 'error' });
                  } finally {
                    setToggleLoading(false);
                  }
                }}
                disabled={toggleLoading}
              >
                {toggleLoading && <CircularProgress size={16} style={{ color: '#fff' }} />}
                به‌روزرسانی
              </button>
              <button
                className="edit-btn cancel"
                style={{ color: '#dc2626', borderColor: '#dc2626' }}
                onClick={() => setRemoveConfirmOpen(true)}
                disabled={toggleLoading}
              >
                حذف بازرس
              </button>
            </>
          ) : (
            <button
              className="edit-btn submit"
              onClick={handleToggleInspector}
              disabled={toggleLoading || !selectedStore?.postalCode}
            >
              {toggleLoading && <CircularProgress size={16} style={{ color: '#fff' }} />}
              تعیین به عنوان بازرس
            </button>
          )}
        </div>
      </Dialog>

      {/* Remove Inspector Confirm */}
      <ConfirmDialog
        open={removeConfirmOpen}
        title="حذف وضعیت بازرس"
        message={
          <span>
            آیا از حذف وضعیت بازرس فروشگاه <strong>«{selectedStore?.name}»</strong> اطمینان دارید؟
            <br />
            این فروشگاه دیگر نمی‌تواند بازرسی انجام دهد.
          </span>
        }
        cancelText="انصراف"
        confirmText="حذف بازرس"
        confirmColor="error"
        loading={toggleLoading}
        onCancel={() => setRemoveConfirmOpen(false)}
        onConfirm={handleToggleInspector}
      />

      {/* Override Result Dialog */}
      <Dialog
        open={overrideDialogOpen}
        onClose={() => setOverrideDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        className="inspector-override-dialog"
      >
        <div className="edit-dialog-title">
          <span>اورراید نتیجه بازرسی</span>
          <button className="details-close" onClick={() => setOverrideDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {selectedInspection && (
            <div className="edit-form">
              <div className="edit-card">
                <div className="edit-card-header">اطلاعات بازرسی</div>
                <div className="edit-card-body">
                  <div className="entity-info" style={{ marginBottom: 8 }}>
                    <span className="entity-name">{selectedInspection.productTitle}</span>
                    <span className="entity-desc">
                      فروشنده: {selectedInspection.storeName}
                    </span>
                    <span className="entity-desc">
                      بازرس: {selectedInspection.inspectionInfo?.inspectorAddress?.storeName || '-'}
                    </span>
                    <span className="entity-desc">
                      وضعیت فعلی:{' '}
                      {INSPECTION_STATUS_MAP[selectedInspection.inspectionInfo?.status]?.label ||
                        selectedInspection.inspectionInfo?.status}
                    </span>
                    {selectedInspection.inspectionInfo?.inspectionResult && (
                      <span className="entity-desc">
                        نتیجه فعلی:{' '}
                        {selectedInspection.inspectionInfo.inspectionResult === 'approved' ? 'تایید' : 'رد'}
                      </span>
                    )}
                  </div>

                  <div
                    style={{
                      background: 'var(--color-warning-bg, #fef3c7)',
                      color: 'var(--color-warning-dark, #92400e)',
                      padding: '10px 12px',
                      borderRadius: 8,
                      fontSize: 13,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <WarningIcon fontSize="small" />
                    اورراید نتیجه بازرسی تنها در شرایط خاص و با تایید مدیریت انجام شود.
                  </div>
                </div>
              </div>

              <div className="edit-card">
                <div className="edit-card-header">تصمیم جدید</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field full">
                      <label>نتیجه جدید</label>
                      <select
                        value={overrideResult}
                        onChange={(e) => setOverrideResult(e.target.value as 'approved' | 'rejected')}
                      >
                        <option value="approved">تایید</option>
                        <option value="rejected">رد</option>
                      </select>
                    </div>
                    <div className="edit-field full">
                      <label>یادداشت ادمین</label>
                      <textarea
                        rows={3}
                        value={overrideNotes}
                        onChange={(e) => setOverrideNotes(e.target.value)}
                        placeholder="دلیل اورراید نتیجه را بنویسید"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => setOverrideDialogOpen(false)}>
            انصراف
          </button>
          <button
            className="edit-btn submit"
            onClick={handleOverrideResult}
            disabled={overrideLoading}
          >
            {overrideLoading && <CircularProgress size={16} style={{ color: '#fff' }} />}
            اورراید نتیجه
          </button>
        </div>
      </Dialog>

      {/* Resolve Penalty Dialog */}
      <Dialog
        open={resolveDialogOpen}
        onClose={() => setResolveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        className="inspector-penalty-dialog"
      >
        <div className="edit-dialog-title">
          <span>{resolveAction === 'approve' ? 'تایید جریمه' : 'بخشودگی جریمه'}</span>
          <button className="details-close" onClick={() => setResolveDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {selectedPenalty && (
            <div className="edit-form">
              <div className="edit-card">
                <div className="edit-card-header">جزئیات جریمه</div>
                <div className="edit-card-body">
                  <div className="entity-info" style={{ marginBottom: 12 }}>
                    <span className="entity-name">{getPenaltyProductTitle(selectedPenalty)}</span>
                    <span className="entity-desc">
                      بازرس: {getPenaltyInspectorName(selectedPenalty)}
                    </span>
                    <span className="entity-desc">
                      نوع: {selectedPenalty.type === 'late_inspection' ? 'تاخیر در بازرسی' : 'تاخیر در ارسال به خریدار'}
                    </span>
                    <span className="entity-desc">
                      روزهای تاخیر: {toPersianDigits(String(selectedPenalty.daysLate))} روز
                    </span>
                  </div>
                  <div
                    style={{
                      background: 'var(--color-error-bg, #fee2e2)',
                      color: 'var(--color-error-dark, #991b1b)',
                      padding: '10px 12px',
                      borderRadius: 8,
                      fontWeight: 700,
                    }}
                  >
                    جمع جریمه: {toPersianDigits(Number(selectedPenalty.totalPenalty).toLocaleString('fa-IR'))} تومان
                  </div>

                  <div
                    style={{
                      background: resolveAction === 'approve'
                        ? 'var(--color-warning-bg, #fef3c7)'
                        : 'var(--color-primary-bg, #FCE8EE)',
                      color: resolveAction === 'approve'
                        ? 'var(--color-warning-dark, #92400e)'
                        : 'var(--color-primary, #A84B6A)',
                      padding: '10px 12px',
                      borderRadius: 8,
                      fontSize: 13,
                      marginTop: 12,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    {resolveAction === 'approve' ? <WarningIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                    {resolveAction === 'approve'
                      ? `با تایید جریمه، مبلغ ${toPersianDigits(Number(selectedPenalty.totalPenalty).toLocaleString('fa-IR'))} تومان از حساب بازرس کسر خواهد شد.`
                      : 'با بخشودگی جریمه، هیچ مبلغی از حساب بازرس کسر نمی‌شود.'}
                  </div>
                </div>
              </div>

              <div className="edit-card">
                <div className="edit-card-header">یادداشت</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field full">
                      <label>یادداشت ادمین (اختیاری)</label>
                      <textarea
                        rows={3}
                        value={resolveNote}
                        onChange={(e) => setResolveNote(e.target.value)}
                        placeholder="دلیل تصمیم‌گیری را بنویسید"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => setResolveDialogOpen(false)}>
            انصراف
          </button>
          <button
            className="edit-btn submit"
            onClick={handleResolvePenalty}
            disabled={resolveLoading}
            style={resolveAction === 'approve' ? { background: '#dc2626' } : { background: '#16a34a' }}
          >
            {resolveLoading && <CircularProgress size={16} style={{ color: '#fff' }} />}
            {resolveAction === 'approve' ? 'تایید جریمه' : 'بخشودگی جریمه'}
          </button>
        </div>
      </Dialog>

      {/* Add Inspector Dialog */}
      <Dialog
        open={addInspectorOpen}
        onClose={() => !addSubmitting && setAddInspectorOpen(false)}
        maxWidth="sm"
        fullWidth
        className="inspector-edit-dialog"
      >
        <div className="edit-dialog-title">
          <span>{addSelectedStore ? 'تعیین تنظیمات بازرس' : 'افزودن بازرس جدید'}</span>
          <button
            className="details-close"
            onClick={() => !addSubmitting && setAddInspectorOpen(false)}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {!addSelectedStore ? (
            <div className="edit-form">
              <div className="edit-card">
                <div className="edit-card-header">جستجوی فروشگاه</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field full">
                      <label>نام فروشگاه</label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input
                          type="text"
                          autoFocus
                          placeholder="نام فروشگاه را وارد کنید..."
                          value={addSearchInput}
                          onChange={(e) => setAddSearchInput(e.target.value)}
                          onKeyPress={handleAddSearchKey}
                          style={{ flex: 1 }}
                        />
                        <button
                          className="edit-btn submit"
                          onClick={handleSearchAddInspector}
                          disabled={addSearchLoading}
                          style={{ minWidth: 90 }}
                        >
                          {addSearchLoading ? (
                            <CircularProgress size={16} style={{ color: '#fff' }} />
                          ) : (
                            <>
                              <SearchIcon fontSize="small" />
                              <span style={{ marginRight: 4 }}>جستجو</span>
                            </>
                          )}
                        </button>
                      </div>
                      <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 6 }}>
                        فقط فروشگاه‌هایی که هنوز بازرس نیستند نمایش داده می‌شوند
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {addSearched && (
                <div className="edit-card">
                  <div className="edit-card-header">
                    نتایج جستجو
                    {!addSearchLoading && ` (${toPersianDigits(String(addSearchResults.length))})`}
                  </div>
                  <div className="edit-card-body" style={{ padding: 0 }}>
                    {addSearchLoading ? (
                      <div style={{ textAlign: 'center', padding: 24 }}>
                        <CircularProgress size={28} />
                      </div>
                    ) : addSearchResults.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: 24, color: 'var(--color-text-secondary)' }}>
                        <StoreIcon style={{ fontSize: 36, opacity: 0.4 }} />
                        <p style={{ marginTop: 8, fontSize: 13 }}>
                          فروشگاه غیربازرس با این نام یافت نشد
                        </p>
                      </div>
                    ) : (
                      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                        {addSearchResults.map((store) => (
                          <div
                            key={store._id}
                            onClick={() => {
                              setAddSelectedStore(store);
                              setAddCommissionSplit(10);
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                              padding: '12px 14px',
                              borderBottom: '1px solid var(--color-border, #eee)',
                              cursor: 'pointer',
                              transition: 'background 0.15s',
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = 'var(--color-hover, #f8f9fa)')
                            }
                            onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                          >
                            <div className="entity-image"><StoreIcon /></div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: 14 }}>{store.name}</div>
                              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                                {store.owner?.username || '-'}
                                {store.owner?.email ? ` • ${store.owner.email}` : ''}
                              </div>
                              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                                {store.province && store.city
                                  ? `${store.province}، ${store.city}`
                                  : 'آدرس ثبت نشده'}
                                {!store.postalCode && (
                                  <span style={{ color: '#dc2626', marginRight: 8 }}>
                                    • بدون کد پستی
                                  </span>
                                )}
                              </div>
                            </div>
                            <div>
                              {store.postalCode ? (
                                <span className="tag tag-sm tag-success">قابل تعیین</span>
                              ) : (
                                <span className="tag tag-sm tag-warning">کد پستی ندارد</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="edit-form">
              <div className="edit-card">
                <div className="edit-card-header">فروشگاه انتخاب شده</div>
                <div className="edit-card-body">
                  <div className="entity-info-cell" style={{ marginBottom: 12 }}>
                    <div className="entity-image"><StoreIcon /></div>
                    <div className="entity-info">
                      <span className="entity-name">{addSelectedStore.name}</span>
                      <span className="entity-desc">
                        مالک: {addSelectedStore.owner?.username || '-'}
                      </span>
                      <span className="entity-desc">
                        آدرس: {addSelectedStore.province && addSelectedStore.city
                          ? `${addSelectedStore.province}، ${addSelectedStore.city}`
                          : 'ثبت نشده'}
                      </span>
                    </div>
                  </div>
                  {!addSelectedStore.postalCode && (
                    <div
                      style={{
                        background: 'var(--color-warning-bg, #fef3c7)',
                        color: 'var(--color-warning-dark, #92400e)',
                        padding: '10px 12px',
                        borderRadius: 8,
                        fontSize: 13,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <WarningIcon fontSize="small" />
                      این فروشگاه کد پستی ندارد. برای فعالیت به عنوان بازرس، کد پستی الزامی است.
                    </div>
                  )}
                </div>
              </div>

              <div className="edit-card">
                <div className="edit-card-header">تنظیمات کمیسیون</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field full">
                      <label>سهم کمیسیون بازرس (%)</label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={addCommissionSplit}
                        onChange={(e) =>
                          setAddCommissionSplit(
                            Math.max(0, Math.min(100, Number(e.target.value)))
                          )
                        }
                      />
                      <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                        درصد کمیسیون فروشنده که به بازرس تعلق می‌گیرد
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        <div className="edit-dialog-actions">
          {addSelectedStore ? (
            <>
              <button
                className="edit-btn cancel"
                onClick={() => setAddSelectedStore(null)}
                disabled={addSubmitting}
              >
                <ArrowBackIcon fontSize="small" />
                <span style={{ marginRight: 4 }}>بازگشت</span>
              </button>
              <button
                className="edit-btn submit"
                onClick={handleConfirmAddInspector}
                disabled={addSubmitting || !addSelectedStore.postalCode}
              >
                {addSubmitting && <CircularProgress size={16} style={{ color: '#fff' }} />}
                تعیین به عنوان بازرس
              </button>
            </>
          ) : (
            <button
              className="edit-btn cancel"
              onClick={() => setAddInspectorOpen(false)}
            >
              انصراف
            </button>
          )}
        </div>
      </Dialog>
    </div>
  );
};

export default InspectorManagement;
